import { useEffect, useState } from "react";
import bifrostService from "../service/bifrost-service";
import { saveBifrostAuthCookies } from "../utils/helpers/auth-cookie";
import { proxyAuthState } from "../service/userAuthApi";

// eslint-disable-next-line max-statements
const useBifrostSsoCallback = ({
    setBifrostTokensConnect,
    setProfileWithTokenConnect,
    setPrimaryIdentifierTypeConnect
} = {}) => {
    const [isProcessing, setIsProcessing] = useState(() => (
        typeof window !== "undefined" && bifrostService.isSsoCallback()
    ));

    /* eslint-disable max-statements */
    useEffect(() => {
        if (!bifrostService.isSsoCallback()) return;

        const code = bifrostService.getSsoCodeFromUrl();
        if (!code) return;

        const returnUrl = bifrostService.getSsoReturnUrl();

        bifrostService.exchangeCodeForTokens(code)
            .then((tokens) => {
                const accessToken = tokens?.token;
                if (!accessToken) {
                    throw new Error("No access token received");
                }

                saveBifrostAuthCookies({
                    accessToken,
                    refreshToken: tokens?.refresh_token,
                    sessionId: tokens?.session_id,
                    expiresIn: tokens?.expires_in
                });

                bifrostService.cleanSsoUrlParams();

                // Determine if we need to navigate to a different page
                const cleanReturn = returnUrl && returnUrl !== window.location.origin
                    ? returnUrl.replace(window.location.origin, "")
                    : "/";
                const willNavigate = cleanReturn !== window.location.pathname;

                // Flag so AUConfiguration can show secondary identifier modal after profile loads
                window.sessionStorage.setItem("bifrost_sso_pending_secondary", "true");

                if (willNavigate) {
                    // Navigate immediately — target page will bootstrap from cookies
                    // via getRefreshTokenInfo, avoiding race between Redux state and navigation
                    window.location.href = cleanReturn;
                    return;
                }

                // Same page — set Redux state directly
                proxyAuthState.accessToken = accessToken;
                proxyAuthState.isLoggedIn = true;

                if (setBifrostTokensConnect) {
                    setBifrostTokensConnect({
                        secureToken: accessToken,
                        refreshToken: tokens?.refresh_token,
                        sessionId: tokens?.session_id
                    });
                }
                if (setProfileWithTokenConnect) {
                    setProfileWithTokenConnect(accessToken);
                }
                if (setPrimaryIdentifierTypeConnect) {
                    setPrimaryIdentifierTypeConnect("email");
                }
            })
            .catch(() => {
                bifrostService.cleanSsoUrlParams();
            })
            .finally(() => {
                setIsProcessing(false);
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    /* eslint-enable max-statements */

    return { isProcessingSsoCallback: isProcessing };
};

export default useBifrostSsoCallback;
